<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiPrefillFormEditor from './AiPrefillFormEditor.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const enabled = ref(false);
const activeFormId = ref(null);
const forms = ref([]);
const extractionDelaySeconds = ref(15);
const contextWindowMessages = ref(10);
const confidenceThreshold = ref(0.75);
const selectedModel = ref('');
const isUpdating = ref(false);

const conversationAttributes = computed(() =>
  store.getters['attributes/getAttributesByModel']('conversation_attribute')
);

const hasFormsConfigured = computed(() => forms.value.length > 0);

function setDefaults() {
  const config = props.inbox.ai_prefill_config || {};
  enabled.value = config.enabled || false;
  activeFormId.value = config.active_form_id || null;
  forms.value = (config.forms || []).map(f => ({
    ...f,
    fields: [...(f.fields || [])],
  }));
  extractionDelaySeconds.value = config.extraction_delay_seconds || 15;
  contextWindowMessages.value = config.context_window_messages || 10;
  confidenceThreshold.value = config.confidence_threshold || 0.75;
  selectedModel.value = config.model || '';
}

function addForm() {
  const id = `form_${Date.now()}`;
  forms.value.push({ id, name: 'New Form', fields: [] });
  if (!activeFormId.value) activeFormId.value = id;
}

function removeForm(formId) {
  forms.value = forms.value.filter(f => f.id !== formId);
  if (activeFormId.value === formId) {
    activeFormId.value = forms.value[0]?.id || null;
  }
}

function updateForm(formId, updatedForm) {
  const idx = forms.value.findIndex(f => f.id === formId);
  if (idx >= 0) forms.value[idx] = updatedForm;
}

function serializeForm(form) {
  return {
    id: form.id,
    name: form.name,
    fields: (form.fields || [])
      .filter(f => f.key && f.display_type)
      .map(f => ({
        key: f.key,
        label: f.label || f.key,
        display_type: f.display_type,
        description: f.description || '',
        regex_pattern: f.display_type === 'text' ? f.regex_pattern || '' : '',
        regex_cue: f.display_type === 'text' ? f.regex_cue || '' : '',
        attribute_values:
          f.display_type === 'list' ? f.attribute_values || [] : [],
      })),
  };
}

async function onSave() {
  isUpdating.value = true;
  try {
    const payload = {
      id: props.inbox.id,
      channel: {},
      ai_prefill_config: {
        enabled: enabled.value,
        active_form_id: activeFormId.value,
        forms: forms.value.map(serializeForm),
        extraction_delay_seconds: Number(extractionDelaySeconds.value),
        context_window_messages: Number(contextWindowMessages.value),
        confidence_threshold: Number(confidenceThreshold.value),
      },
    };
    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.AI_PREFILL.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.AI_PREFILL.SAVE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
}

watch(() => props.inbox, setDefaults, { immediate: true });
watch(() => conversationAttributes.value?.length, setDefaults);
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.AI_PREFILL.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.AI_PREFILL.SUBTITLE') }}
        </p>
      </div>
      <label class="flex items-center gap-2 cursor-pointer">
        <input v-model="enabled" type="checkbox" />
        <span class="text-sm">{{ $t('INBOX_MGMT.AI_PREFILL.ENABLE') }}</span>
      </label>
    </div>

    <div v-if="enabled" class="space-y-6">
      <!-- Settings Grid -->
      <div class="grid grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('INBOX_MGMT.AI_PREFILL.EXTRACTION_DELAY') }}
          </label>
          <input
            v-model.number="extractionDelaySeconds"
            type="number"
            min="5"
            max="300"
            class="!mb-0"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('INBOX_MGMT.AI_PREFILL.CONTEXT_WINDOW') }}
          </label>
          <input
            v-model.number="contextWindowMessages"
            type="number"
            min="1"
            max="50"
            class="!mb-0"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('INBOX_MGMT.AI_PREFILL.CONFIDENCE_THRESHOLD') }}
          </label>
          <input
            v-model.number="confidenceThreshold"
            type="number"
            min="0"
            max="1"
            step="0.05"
            class="!mb-0"
          />
        </div>
      </div>

      <!-- Forms Section -->
      <div>
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.AI_PREFILL.FORMS_TITLE') }}
          </h4>
          <NextButton sm faded icon="i-lucide-plus" @click="addForm">
            {{ $t('INBOX_MGMT.AI_PREFILL.ADD_FORM') }}
          </NextButton>
        </div>

        <div
          v-if="!hasFormsConfigured"
          class="text-sm text-n-slate-11 p-4 bg-n-alpha-2 rounded-lg text-center"
        >
          {{ $t('INBOX_MGMT.AI_PREFILL.NO_FORMS') }}
        </div>

        <div v-for="form in forms" :key="form.id" class="mb-4">
          <AiPrefillFormEditor
            :form="form"
            :is-active="form.id === activeFormId"
            :conversation-attributes="conversationAttributes"
            @update="updatedForm => updateForm(form.id, updatedForm)"
            @remove="removeForm(form.id)"
            @set-active="activeFormId = form.id"
          />
        </div>
      </div>

      <div class="flex justify-end">
        <NextButton :loading="isUpdating" @click="onSave">
          {{ $t('INBOX_MGMT.AI_PREFILL.SAVE') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
